import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getAllBusinesses, getBusinessCategories, getPopularProducts, getFeaturedProducts } from '../services/businessService';
import { getSettings } from '../services/settingsService';

const BusinessesContext = createContext(null);

export function BusinessesProvider({ children }) {
  const [businesses, setBusinesses] = useState([]);
  const [backendCategories, setBackendCategories] = useState([]);
  const [popularProducts, setPopularProducts] = useState({});
  const [featuredProducts, setFeaturedProducts] = useState({});
  const [settings, setSettings] = useState(null);
  const [homeTag, setHomeTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch negocios + categorías + settings + productos populares — una sola vez
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [businessesData, categoriesData, settingsData, popularData, featuredData] = await Promise.all([
          getAllBusinesses(),
          getBusinessCategories('comida').catch(() => []),
          getSettings().catch(() => null),
          getPopularProducts().catch(() => ({})),
          getFeaturedProducts().catch(() => ({})),
        ]);

        setBusinesses(businessesData || []);
        setBackendCategories(categoriesData || []);
        setPopularProducts(popularData || {});
        setFeaturedProducts(featuredData || {});
        setSettings(settingsData || null);

        if (settingsData?.homeTag) {
          setHomeTag(settingsData.homeTag);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await getAllBusinesses();
      setBusinesses(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(() => ({
    businesses,
    backendCategories,
    popularProducts,
    featuredProducts,
    settings,
    homeTag,
    loading,
    error,
    refresh,
  }), [businesses, backendCategories, popularProducts, featuredProducts, settings, homeTag, loading, error]);

  return (
    <BusinessesContext.Provider value={value}>
      {children}
    </BusinessesContext.Provider>
  );
}

export function useBusinessesContext() {
  const context = useContext(BusinessesContext);
  if (!context) {
    throw new Error('useBusinessesContext must be used within BusinessesProvider');
  }
  return context;
}
